import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { countryBanService } from './services/countryBanService';
import CountryBlockedPage from './components/auth/CountryBlockedPage';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import { lazyRoute } from './components/common/lazyRoute';
import {
  DashboardRouteShell,
  FeedRouteShell,
  FormRouteShell,
  ListRouteShell,
  BacklinkRouteShell,
  OrdersRouteShell,
  SitesRouteShell,
  TableRouteShell,
} from './components/common/routeShells';
import SuperAdminPasswordGate from './components/auth/SuperAdminPasswordGate';
import PermissionRoute from './components/auth/PermissionRoute';

const sitesFallback = <SitesRouteShell />;
const ordersFallback = <OrdersRouteShell />;
const dashFallback = <DashboardRouteShell />;

const LoginPage = lazy(() => import('./pages/LoginPage'));
const PublisherLoginPage = lazy(() => import('./pages/PublisherLoginPage'));
const PortalLoginPage = lazy(() => import('./pages/PortalLoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const DashboardPage = lazyRoute('/dashboard', () => import('./pages/DashboardPage'), dashFallback);
const ClientsPage = lazyRoute(
  '/clients',
  () => import('./pages/ClientsPage'),
  <TableRouteShell
    title="Clients"
    subtitle="Manage your agency clients and assignments"
    searchPlaceholder="Search clients…"
    columns={['Name', 'Email', 'Phone', 'Company', 'Country', 'Status', 'Created', 'Actions']}
  />,
);
const ProjectsPage = lazyRoute(
  '/projects',
  () => import('./pages/ProjectsPage'),
  <TableRouteShell
    title="Projects"
    searchPlaceholder="Search projects…"
    stats={['Total Projects', 'Active Orders', 'Live Placements', 'Total Budget / mo']}
    columns={['Project Name', 'Client', 'Budget', 'Placements', 'Actions']}
  />,
);
const PublishersPage = lazyRoute(
  '/publishers',
  () => import('./pages/PublishersPage'),
  <TableRouteShell
    title="Publishers"
    searchPlaceholder="Search publishers by name or email…"
    stats={['Total Publishers', 'Active', 'Email Contacts', 'WhatsApp Contacts']}
    columns={['Publisher', 'Email', 'Sites', 'Status', 'Actions']}
  />,
);
const GuestBlogSitesPage = lazyRoute('/sites', () => import('./pages/GuestBlogSitesPage'), sitesFallback);
const PlanningPage = lazyRoute(
  '/planning',
  () => import('./pages/PlanningPage'),
  <ListRouteShell title="Planning" searchPlaceholder="Search plans…" />,
);
const OrdersPage = lazyRoute(
  '/orders',
  () => import('./pages/OrdersPage'),
  <TableRouteShell
    title="Orders"
    searchPlaceholder="Search orders…"
    stats={['Total Orders', 'Live Orders', 'Pending / In Review', 'Total Revenue']}
    columns={['Order', 'Client', 'Project', 'Site', 'Status', 'Price', 'Actions']}
  />,
);
const DataEntryPage = lazyRoute(
  '/data-entry',
  () => import('./pages/DataEntryPage'),
  <TableRouteShell
    title="Data Entry"
    searchPlaceholder="Search…"
    columns={['Site', 'Status', 'Publisher', 'Updated', 'Actions']}
  />,
);
const SitesPage = lazyRoute(
  '/pricing-sites',
  () => import('./pages/SitesPage'),
  <TableRouteShell
    title="Pricing Sites"
    searchPlaceholder="Search sites…"
    columns={['Site', 'DR', 'Traffic', 'Price', 'Status']}
  />,
);
const GuestBlogPlacementsPage = lazyRoute(
  '/guest-blog-placements',
  () => import('./pages/GuestBlogPlacementsPage'),
  ordersFallback,
);
const ReportsPage = lazyRoute(
  '/reports',
  () => import('./pages/ReportsPage'),
  <DashboardRouteShell title="Reports" />,
);
const UsersPage = lazyRoute(
  '/users',
  () => import('./pages/UsersPage'),
  <TableRouteShell
    title="Users"
    searchPlaceholder="Search users…"
    columns={['Name', 'Email', 'Role', 'Status', 'Actions']}
  />,
);
const AuditPage = lazyRoute(
  '/audit',
  () => import('./pages/AuditPage'),
  <FeedRouteShell title="Audit Logs" subtitle="Recent account and data activity" />,
);
const PendingSitesPage = lazyRoute(
  '/pending-sites',
  () => import('./pages/PendingSitesPage'),
  <TableRouteShell
    title="Pending Sites"
    searchPlaceholder="Search sites…"
    columns={['Site', 'Publisher', 'Submitted', 'Status', 'Actions']}
  />,
);
const PriceChangeRequestsPage = lazyRoute(
  '/price-change-requests',
  () => import('./pages/PriceChangeRequestsPage'),
  <TableRouteShell
    title="Price Requests"
    searchPlaceholder="Search requests…"
    columns={['Site', 'Publisher', 'Current', 'Requested', 'Status', 'Actions']}
  />,
);
const PublisherBillingPage = lazyRoute(
  '/publisher-billing',
  () => import('./pages/PublisherBillingPage'),
  <TableRouteShell
    title="Billing"
    searchPlaceholder="Search…"
    columns={['Period', 'Placements', 'Amount', 'Status']}
  />,
);
const AdminBillingPage = lazyRoute(
  '/admin-billing',
  () => import('./pages/AdminBillingPage'),
  <DashboardRouteShell title="Billing" />,
);
const ClientBillingPage = lazyRoute(
  ['/billing', '/invoices'],
  () => import('./pages/ClientBillingPage'),
  <ListRouteShell title="Billing" subtitle="Invoices and payment methods" searchPlaceholder="Search invoices…" />,
);
const LiveLinkStatusPage = lazyRoute(
  '/live-link-status',
  () => import('./pages/LiveLinkStatusPage'),
  <BacklinkRouteShell />,
);
const SettingsPage = lazyRoute(
  '/settings',
  () => import('./pages/SettingsPage'),
  <FormRouteShell title="Settings" fields={8} />,
);
const ClientRequestedSitesPage = lazyRoute(
  '/requested-sites',
  () => import('./pages/ClientRequestedSitesPage'),
  <TableRouteShell
    title="Requested Sites"
    searchPlaceholder="Search sites…"
    columns={['Site', 'Project', 'Status', 'Requested', 'Actions']}
  />,
);
const DrCalculatorSiteRequestsPage = lazyRoute(
  '/competitor-backlink-site-requests',
  () => import('./pages/DrCalculatorSiteRequestsPage'),
  <TableRouteShell
    title="Site Requests"
    searchPlaceholder="Search…"
    columns={['Site', 'Client', 'Status', 'Requested']}
  />,
);
const ProjectHistoricalUsagePage = lazyRoute(
  '/historical-usage',
  () => import('./pages/ProjectHistoricalUsagePage'),
  <TableRouteShell
    title="Historical Usage"
    searchPlaceholder="Search…"
    columns={['Site', 'Order', 'Status', 'Price', 'Date']}
  />,
);
const ProjectOverviewPage = lazyRoute(
  '/project-workflow/overview',
  () => import('./pages/ProjectOverviewPage'),
  <DashboardRouteShell title="Overview" />,
);
const ProjectAIAssistedSetupPage = lazyRoute(
  '/project-workflow/ai-setup',
  () => import('./pages/ProjectAIAssistedSetupPage'),
  <FormRouteShell title="AI setup" fields={6} />,
);
const ProjectReportingPage = lazyRoute(
  '/project-workflow/reporting',
  () => import('./pages/ProjectReportingPage'),
  <DashboardRouteShell title="Reporting" />,
);
const ProfilePage = lazyRoute(
  '/profile',
  () => import('./pages/ProfilePage'),
  <FormRouteShell title="Profile" fields={6} />,
);
const PlaceOrderPage = lazyRoute(
  '/project-workflow/place-order',
  () => import('./pages/PlaceOrderPage'),
  <ListRouteShell title="Place order" subtitle="Add a document link for each site" />,
);
const ProjectCartPage = lazyRoute(
  '/project-workflow/cart',
  () => import('./pages/ProjectCartPage'),
  <ListRouteShell title="Cart & Checkout" />,
);
const RestoreCenterPage = lazyRoute(
  '/archive',
  () => import('./pages/settings/RestoreCenterPage'),
  <TableRouteShell
    title="Archive"
    subtitle="Restore archived items"
    searchPlaceholder="Search archive…"
    columns={['Item', 'Type', 'Archived', 'By', 'Actions']}
  />,
);

function RouteFallback() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
      <LoadingSpinner message="" />
    </Box>
  );
}

function isSafeNextPath(next: string): boolean {
  return next.startsWith('/') && !next.startsWith('//');
}

const STAFF_LOGIN_PREFIXES = [
  '/audit',
  '/users',
  '/settings',
  '/admin-billing',
  '/pending-sites',
  '/price-change-requests',
  '/clients',
  '/publishers',
  '/project-workflow',
  '/dashboard',
  '/orders',
  '/guest-blog',
  '/sites',
  '/planning',
  '/data-entry',
  '/reports',
  '/live-link-status',
  '/profile',
];

function loginRedirectPath(pathname: string, search: string): string {
  if (pathname === '/' || pathname === '/login') return '/login';
  if (pathname === '/login-administrator') return '/login-administrator';
  const next = `${pathname}${search}`;
  if (!isSafeNextPath(next)) return '/login';

  // Prefer how they signed in (survives when token is cleared but loginType remains).
  let loginType: string | null = null;
  try {
    loginType = localStorage.getItem('loginType');
  } catch {
    loginType = null;
  }
  if (loginType === 'publisher') {
    return `/publisher?next=${encodeURIComponent(next)}`;
  }
  if (loginType === 'portal' || loginType === 'client' || loginType === 'agency') {
    return `/login?next=${encodeURIComponent(next)}`;
  }
  if (loginType === 'admin') {
    return `/login-administrator?next=${encodeURIComponent(next)}`;
  }

  // Fallback by path when loginType is missing
  const staff = STAFF_LOGIN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  const loginPath = staff ? '/login-administrator' : '/login';
  return `${loginPath}?next=${encodeURIComponent(next)}`;
}

function ContinueFromAdminLogin(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next') || '';
  // Admin / Super Admin default: Orders (placements), not Dashboard
  return <Navigate to={isSafeNextPath(next) ? next : '/guest-blog-placements'} replace />;
}

const GuestBlogSitesRoute: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const projectId = search.get('projectId') || '';
  const hasWorkflowContext = Boolean(projectId || search.get('planId'));

  if (user?.role === 'AGENCY' && !hasWorkflowContext) {
    return <Navigate to="/dashboard" replace />;
  }
  // Remount when entering/leaving a project Explore context so page size resets to 50
  return <GuestBlogSitesPage key={projectId ? `explore-${projectId}` : 'sites-catalog'} />;
};

function App() {
  const { user, loading, hasPermission, isSuperAdmin } = useAuth();
  const location = useLocation();

  const adminCan = (key: string) =>
    isSuperAdmin || (user?.role === 'ADMIN' && hasPermission(key));

  const countryAccess = useQuery({
    queryKey: ['country-access'],
    queryFn: () => countryBanService.checkAccess(),
    staleTime: 30_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  if (loading || countryAccess.isLoading) {
    // /sites: never show a lone card skeleton (looks like a broken full-page load).
    // Wait for auth, then GuestBlogSitesPage shows real chrome + data-only skeleton.
    if (location.pathname.startsWith('/sites')) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            background: 'var(--bg, #f5f7fb)',
          }}
        />
      );
    }
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <LoadingSpinner message="" />
      </Box>
    );
  }

  const blockedCountry = countryAccess.data?.countryCode;
  const superAdminBlocked = blockedCountry === 'PK' || blockedCountry === 'BD';
  if (countryAccess.data?.blocked && (!isSuperAdmin || superAdminBlocked)) {
    if (!superAdminBlocked && location.pathname.startsWith('/login-administrator')) {
      return (
        <Suspense fallback={<RouteFallback />}>
          <LoginPage />
        </Suspense>
      );
    }
    return <CountryBlockedPage countryCode={blockedCountry} />;
  }

  if (!user) {
    return (
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login-administrator" element={<LoginPage />} />
        <Route path="/publisher" element={<PublisherLoginPage />} />
        <Route path="/login" element={<PortalLoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/client" element={<Navigate to="/login" replace />} />
        <Route path="/agency" element={<Navigate to="/login" replace />} />
        <Route
          path="*"
          element={<Navigate to={loginRedirectPath(location.pathname, location.search)} replace />}
        />
      </Routes>
      </Suspense>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login-administrator" element={<ContinueFromAdminLogin />} />
        <Route path="/publisher" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/register" element={<Navigate to="/dashboard" replace />} />
        <Route path="/verify-email" element={<Navigate to="/dashboard" replace />} />
        <Route path="/client" element={<Navigate to="/dashboard" replace />} />
        <Route path="/agency" element={<Navigate to="/dashboard" replace />} />

        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/project-workflow/overview" element={<ProjectOverviewPage />} />
        <Route path="/project-workflow/ai-setup" element={<ProjectAIAssistedSetupPage />} />
        <Route path="/project-workflow/reporting" element={<ProjectReportingPage />} />
        <Route path="/project-workflow/place-order" element={<PlaceOrderPage />} />
        {/* Create Order step removed — redirect legacy URLs to Place Order */}
        <Route
          path="/project-workflow/plan"
          element={<Navigate to={`/project-workflow/place-order${window.location.search}`} replace />}
        />
        <Route path="/project-workflow/cart" element={<ProjectCartPage />} />

        {(user.role === 'AGENCY' || adminCan('clients')) && user.role !== 'PUBLISHER' && user.role !== 'CLIENT' && (
          <Route path="/clients/*" element={<ClientsPage />} />
        )}

        {user.role !== 'PUBLISHER' && (user.role === 'CLIENT' || user.role === 'AGENCY' || adminCan('projects')) && (
          <Route path="/projects/*" element={<ProjectsPage />} />
        )}

        {(user.role === 'CLIENT' || user.role === 'AGENCY' || adminCan('planning')) && (
          <Route path="/planning/*" element={<PlanningPage />} />
        )}

        {user.role === 'PUBLISHER' && <Route path="/publisher-billing" element={<PublisherBillingPage />} />}

        {adminCan('publishers') && <Route path="/publishers/*" element={<PublishersPage />} />}

        {(user.role === 'CLIENT' || user.role === 'AGENCY' || user.role === 'PUBLISHER' || adminCan('sites')) && (
          <Route path="/sites/*" element={<GuestBlogSitesRoute />} />
        )}

        {adminCan('historical_usage') && (
          <Route path="/projects/:projectId/historical-usage" element={<ProjectHistoricalUsagePage />} />
        )}

        {adminCan('pricing_sites') && <Route path="/pricing-sites/*" element={<SitesPage />} />}
        {adminCan('data_entry') && <Route path="/data-entry/*" element={<DataEntryPage />} />}

        {adminCan('orders') && user.role !== 'AGENCY' && user.role !== 'CLIENT' && (
          <Route path="/orders/*" element={<OrdersPage />} />
        )}

        {(user.role === 'CLIENT' || user.role === 'AGENCY' || user.role === 'PUBLISHER' || adminCan('placements')) && (
          <Route path="/guest-blog-placements/*" element={<GuestBlogPlacementsPage />} />
        )}

        {(user.role === 'CLIENT' || user.role === 'AGENCY' || user.role === 'PUBLISHER' || adminCan('monitoring')) && (
          <Route path="/live-link-status/*" element={<LiveLinkStatusPage />} />
        )}

        {adminCan('reports') && <Route path="/reports/*" element={<ReportsPage />} />}

        {adminCan('archive') && <Route path="/archive" element={<RestoreCenterPage variant="self" />} />}

        {user.role === 'AGENCY' && (
          <>
            <Route path="/invoices/*" element={<ClientBillingPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/archive" element={<RestoreCenterPage variant="self" />} />
          </>
        )}

        {user.role === 'CLIENT' && (
          <>
            {/* AI Suggestions & Competitor Analysis temporarily hidden */}
            {/* <Route path="/competitor-backlink-analysis" element={<DRCalculatorPage />} /> */}
            {/* <Route path="/dr-calculator" element={<Navigate to="/competitor-backlink-analysis" replace />} /> */}
            {/* <Route path="/ai-suggestions" element={<AISuggestionsPage />} /> */}
            <Route path="/requested-sites" element={<ClientRequestedSitesPage />} />
            <Route path="/billing" element={<ClientBillingPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/archive" element={<RestoreCenterPage variant="self" />} />
          </>
        )}

        {adminCan('competitor_requests') && (
          <>
            <Route path="/competitor-backlink-site-requests" element={<DrCalculatorSiteRequestsPage />} />
            <Route path="/dr-calculator-site-requests" element={<Navigate to="/competitor-backlink-site-requests" replace />} />
          </>
        )}

        {adminCan('pending_sites') && <Route path="/pending-sites/*" element={<PendingSitesPage />} />}
        {adminCan('price_change') && <Route path="/price-change-requests" element={<PriceChangeRequestsPage />} />}

        {isSuperAdmin && (
          <Route
            path="/invoices/*"
            element={
              <SuperAdminPasswordGate>
                <ClientBillingPage />
              </SuperAdminPasswordGate>
            }
          />
        )}
        {adminCan('invoices') && !isSuperAdmin && (
          <Route path="/invoices/*" element={<ClientBillingPage />} />
        )}

        {adminCan('users') && <Route path="/users/*" element={<UsersPage />} />}
        {adminCan('audit') && <Route path="/audit/*" element={<AuditPage />} />}

        {isSuperAdmin && (
          <Route
            path="/admin-billing"
            element={
              <SuperAdminPasswordGate>
                <AdminBillingPage />
              </SuperAdminPasswordGate>
            }
          />
        )}
        {adminCan('admin_billing') && !isSuperAdmin && (
          <Route path="/admin-billing" element={<AdminBillingPage />} />
        )}

        {adminCan('settings') && <Route path="/settings/*" element={<SettingsPage />} />}

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
